import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import React, { useContext, useState } from "react";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { UPDATE_USER } from "../../../graphql/mutation";
import { ContentBody } from "../../../library/basicComponents";
import { openDialog, validateUserInputs } from "../../../library/helpers";
// import { setLoaderStatus } from "../../../redux/slices/loaderSlice";
import { UserContext } from "../../../amplify/authenticator-provider";
import { ME } from "../../../graphql/query";
import useLang from "../../../hooks/useLang";

import UserProfileForm from "./userProfileForm";
import { parseUserSettings } from "../../../utils/userHelpers";
import SettingSectionWrapper from "../../../wrappers/SettingSectionWrapper";

const UserProfile = ({ userData, refreshUsers, className }) => {
  const { user: userContext, setUser: setUserContext } = useContext(UserContext);
  const [lang] = useLang();
  const [user, setUser] = useState(userContext);

  // ME is the single source for local `user` — onCompleted runs on initial load
  // and on every refetchMe() (after a successful save).
  const { refetch: refetchMe } = useQuery(ME, {
    fetchPolicy: "network-only",
    onCompleted: res => {
      setUser(res.me);
    },
  });

  // NOTE: refreshUsers() is intentionally NOT called in onCompleted — the mutation
  // resolves with HTTP 200 even on a business failure (statusCode !== 200). We only
  // refetch/update context inside the success branch of update() below.
  const [updateUser, { loading: loader }] = useMutation(UPDATE_USER);

  const update = async (updatedUserData = null) => {
    // Use provided user data or fall back to current state
    const userToUpdate = updatedUserData || user;

    // The drawer already uploaded any new profile picture to S3, so use it as-is
    const { profilePic } = userToUpdate;

    // Parse existing settings
    const existingSettings = parseUserSettings(userToUpdate?.settings);

    try {
      const res = await updateUser({
        variables: {
          userId: userToUpdate.id,
          firstName: userToUpdate.firstName,
          lastName: userToUpdate.lastName,
          email:
            userToUpdate.email !== (userData?.email || userContext?.email)
              ? userToUpdate.email
              : null,
          oldPass: userToUpdate.oldPass,
          newPass: userToUpdate.newPass,
          phone: userToUpdate.phone,
          locationId: userToUpdate.locationId,
          profilePic: profilePic,
          preferredLanguageCode: userToUpdate.preferredLanguageCode,
          isTranslationEnabled: userToUpdate?.isTranslationEnabled,
          companyId: userToUpdate.companyId,
          // Each save handler builds the complete settings object (preferredName,
          // PHONE_NUMBERS, DS_AGENT, …) — send it as-is so nothing gets wiped.
          settings: existingSettings,
          // CRM mapping (only present when saved from the CRM tab)
          ...(userToUpdate.dsUser ? { dsUser: userToUpdate.dsUser } : {}),
        },
      });

      if (res?.data?.updateUser?.statusCode !== 200) {
        message.error(res?.data?.updateUser?.message || "");
        // Throw so callers (e.g. the edit drawer) keep their loader / stay open
        throw new Error(res?.data?.updateUser?.message || "Update failed");
      }

      // Success (statusCode === 200): pull the latest user from ME (authoritative),
      // update local state + context, and refetch the users list.
      message.success(lang.userUpdated);
      // refetch ME for the latest user, then update local state + context.
      // (onCompleted does NOT reliably fire on refetch, so set from the result here.)
      const { data: meData } = await refetchMe();
      if (meData?.me) {
        setUser(meData.me);
        setUserContext({
          ...meData.me,
          accessControlPermissions: userContext.accessControlPermissions,
        });
      }
      // refreshUsers();
      return res;
    } catch (err) {
      console.log("Error in updateuser", err);
      throw err; // propagate so the caller can handle (keep drawer open)
    }
  };

  const submitUser = () => {
    if (user?.phone) {
      if (!isPossiblePhoneNumber(user?.phone)) {
        message.error(lang.enterValidPhone);
      } else if (user.newPass !== user.newPassRep) {
        message.error(lang.repeatPassError);
      } else if (validateUserInputs(user)) {
        update();
      } else {
        message.error(lang.invalidFields);
      }
    } else {
      openDialog("phoneCofirmation", {
        handleConfirm: update,
      });
    }
  };

  return (
    <SettingSectionWrapper className={className}>
      {user && <UserProfileForm user={user} onSave={update} />}
    </SettingSectionWrapper>
  );
};

export default UserProfile;
